import React from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined';
import PlaylistAddRoundedIcon from '@mui/icons-material/PlaylistAddRounded';
import { ActivePackageItem } from './newInvoiceForm.helper';

export interface DonorPackageSelectProps {
  packages: ActivePackageItem[];
  loading?: boolean;
  disabled?: boolean;
  label?: string;
  /** Currently selected package (controlled) */
  value?: ActivePackageItem | null;
  onSelect: (pkg: ActivePackageItem) => void;
  /** Called when the user clears the selection */
  onClear?: () => void;
  /** Called when user clicks the reopen icon on an already-selected package */   
  onReopen?: () => void;
}

const DonorPackageSelect: React.FC<DonorPackageSelectProps> = ({
  packages,
  loading = false,
  disabled = false,
  label = 'Add Package Item',
  value = null,
  onSelect,
  onClear,
  onReopen,
}) => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Autocomplete
        size="small"
        options={packages}
        value={value}
        getOptionLabel={(pkg) => {
          const name = pkg.package_name ?? pkg.name ?? '';
          const patient = pkg.patient_name ? ` — ${pkg.patient_name}` : '';
          return `${name}${patient}`;
        }}
        isOptionEqualToValue={(opt, val) => opt.id === val.id}
        loading={loading}
        disabled={disabled}
        onChange={(_e, pkg) => {
          if (pkg) onSelect(pkg);
          else onClear?.();
        }}
        renderInput={(params) => (
          <Tooltip
            title={value ? (value.package_name ?? value.name ?? '') : ''}
            arrow
            placement="top"
            disableHoverListener={!value}
          >
            <TextField
              {...params}
              label={label}
              placeholder={loading ? 'Loading...' : label}
            />
          </Tooltip>
        )}
        renderOption={(props, pkg) => (
          <li {...props} key={pkg.id}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.25 }}>
              <LocalHospitalOutlinedIcon
                sx={{ fontSize: 18, color: '#5A8AEA', mt: 0.25, flexShrink: 0 }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, lineHeight: 1.3 }}
                  noWrap
                >
                  {pkg.package_name ?? pkg.name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                  {pkg.patient_name && (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {pkg.patient_name}
                    </Typography>
                  )}
                  {pkg.patient_mrn && (
                    <Chip
                      label={pkg.patient_mrn}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: 11,
                        fontWeight: 500,
                        bgcolor: '#F0F4FF',
                        color: '#5A8AEA',
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </li>
        )}
        noOptionsText="No packages found"
        blurOnSelect
        sx={{
          flex: 1,
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
          },
        }}
      />
      {value && onReopen && (
        <Tooltip title="Open package services" arrow>
          <IconButton
            size="small"
            onClick={onReopen}
            sx={{
              color: '#5A8AEA',
              border: '1px solid #E4E7EC',
              borderRadius: 1.5,
              p: 0.5,
              '&:hover': { borderColor: '#5A8AEA', bgcolor: '#F0F4FF' },
            }}
          >
            <PlaylistAddRoundedIcon sx={{ fontSize: 24 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

export default DonorPackageSelect;
